'use client';

import { useQuery } from '@tanstack/react-query';
import {
  LeadEventsApi,
  type LeadEventWithStatusDto,
  LeadEventRosterMode,
  TeamsApi,
  type TeamWithCountsDto,
} from '@up2stay/up2promo-api-client';
import { Button, Input, Label, Textarea } from '@up2stay/up2promo-ui';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  eventRangeDayCount,
  isUnusuallyLongEventRange,
  isValidEventDateRange,
  LEAD_EVENT_NAME_MAX,
  toLocalInputValue,
} from '@/core/components/leads/lead-event-form-utils';
import { LeadEventRosterFields } from '@/core/components/leads/lead-event-roster-fields';
import { LogoUpload } from '@/core/components/settings/logo-upload';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { useWorkspaceMembersRoster } from '@/core/hooks/use-workspace-members-roster';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { teamsQueryKeys } from '@/core/query-keys/workspace-members-query-keys';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import { getApiBusinessErrorCode } from '@/core/utils/api-error.utils';

type LeadEventDetailsFormProps = {
  workspaceId: string;
  event: LeadEventWithStatusDto;
  canManage: boolean;
  onSaved: (event: LeadEventWithStatusDto) => void;
  onDelete: () => void;
};

function eventFormSyncKey(event: LeadEventWithStatusDto): string {
  return [
    event.id,
    event.name,
    event.location ?? '',
    event.startsAt,
    event.endsAt,
    event.description ?? '',
    event.rosterMode ?? '',
    (event.rosterUserIds ?? []).join(','),
    (event.rosterTeamIds ?? []).join(','),
    event.coverImageAssetId ?? '',
    event.coverImage?.path ?? '',
  ].join('\0');
}

export function LeadEventDetailsForm({
  workspaceId,
  event,
  canManage,
  onSaved,
  onDelete,
}: LeadEventDetailsFormProps) {
  const dict = useDictionary().leads.events;
  const formDict = dict.form;
  const locale = useLocale();

  const [name, setName] = useState(event.name);
  const [location, setLocation] = useState(event.location ?? '');
  const [startsAt, setStartsAt] = useState(toLocalInputValue(event.startsAt));
  const [endsAt, setEndsAt] = useState(toLocalInputValue(event.endsAt));
  const [description, setDescription] = useState(event.description ?? '');
  const [rosterMode, setRosterMode] = useState<LeadEventRosterMode>(
    (event.rosterMode as LeadEventRosterMode) ?? LeadEventRosterMode.EVERYONE,
  );
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    event.rosterUserIds ?? [],
  );
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(
    event.rosterTeamIds ?? [],
  );
  const [coverAssetId, setCoverAssetId] = useState<string | null>(
    event.coverImageAssetId ?? null,
  );
  const [coverPreview, setCoverPreview] = useState<string | undefined>(
    event.coverImage?.path
      ? getAbsoluteFileUrl(event.coverImage.path)
      : undefined,
  );
  const [submitting, setSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [appliedSyncKey, setAppliedSyncKey] = useState(() =>
    eventFormSyncKey(event),
  );
  const nextSyncKey = eventFormSyncKey(event);

  if (nextSyncKey !== appliedSyncKey) {
    setAppliedSyncKey(nextSyncKey);
    setName(event.name);
    setLocation(event.location ?? '');
    setStartsAt(toLocalInputValue(event.startsAt));
    setEndsAt(toLocalInputValue(event.endsAt));
    setDescription(event.description ?? '');
    setRosterMode(
      (event.rosterMode as LeadEventRosterMode) ?? LeadEventRosterMode.EVERYONE,
    );
    setSelectedUserIds(event.rosterUserIds ?? []);
    setSelectedTeamIds(event.rosterTeamIds ?? []);
    setCoverAssetId(event.coverImageAssetId ?? null);
    setCoverPreview(
      event.coverImage?.path
        ? getAbsoluteFileUrl(event.coverImage.path)
        : undefined,
    );
  }

  const { data: members = [] } = useWorkspaceMembersRoster(
    workspaceId,
    'event-details',
    { enabled: canManage, staleTime: 60_000 },
  );

  const { data: teams = [] } = useQuery({
    queryKey: teamsQueryKeys.roster(workspaceId, 'event-details'),
    queryFn: async () => {
      const res = await TeamsApi.findAll({
        path: { workspaceId },
        query: { page: 1, limit: 100 },
      });
      return (res.data?.data ?? []) as TeamWithCountsDto[];
    },
    enabled: canManage && !!workspaceId,
    staleTime: 60_000,
  });

  const rosterLabels = useMemo(
    () => ({
      rosterMode: formDict.rosterMode,
      rosterEveryone: formDict.rosterEveryone,
      rosterEveryoneHint: formDict.rosterEveryoneHint,
      rosterSelected: formDict.rosterSelected,
      rosterSelectedHint: formDict.rosterSelectedHint,
      selectUsers: formDict.selectUsers,
      selectTeams: formDict.selectTeams,
      searchMembers: formDict.searchMembers,
      searchTeams: formDict.searchTeams,
      noMembersMatch: formDict.noMembersMatch,
      noTeamsMatch: formDict.noTeamsMatch,
      resolvedCount: formDict.resolvedCount,
      resolvedCountOne: formDict.resolvedCountOne,
      rosterEmptyHint: formDict.rosterEmptyHint,
    }),
    [formDict],
  );

  const dateRangeValid = isValidEventDateRange(startsAt, endsAt);
  const showDateRangeError = Boolean(startsAt && endsAt) && !dateRangeValid;
  const rangeDayCount = eventRangeDayCount(startsAt, endsAt);
  const showLongRangeWarning = isUnusuallyLongEventRange(startsAt, endsAt);
  const rosterSelectedEmpty =
    rosterMode === LeadEventRosterMode.SELECTED &&
    selectedUserIds.length === 0 &&
    selectedTeamIds.length === 0;

  const onSubmit = async () => {
    setShowValidation(true);
    if (!canManage) return;
    if (!name.trim()) {
      nameInputRef.current?.focus();
      return;
    }
    if (!dateRangeValid || !startsAt || !endsAt) {
      document.getElementById('event-edit-end')?.focus();
      return;
    }
    if (rosterSelectedEmpty) return;
    setSubmitting(true);
    try {
      const res = await LeadEventsApi.update({
        path: { workspaceId, eventId: event.id },
        body: {
          name: name.trim(),
          location: location.trim() || null,
          startsAt: new Date(startsAt).toISOString(),
          endsAt: new Date(endsAt).toISOString(),
          description: description.trim() || null,
          coverImageAssetId: coverAssetId,
          rosterMode,
          rosterUserIds:
            rosterMode === LeadEventRosterMode.SELECTED ? selectedUserIds : [],
          rosterTeamIds:
            rosterMode === LeadEventRosterMode.SELECTED ? selectedTeamIds : [],
        },
      });
      if (res.data) onSaved(res.data);
    } catch (err: unknown) {
      const code = getApiBusinessErrorCode(err);
      const mapped =
        code && code in formDict.errors
          ? formDict.errors[code as keyof typeof formDict.errors]
          : undefined;
      toast.error(mapped ?? formDict.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!canManage) {
    return (
      <div className="flex flex-col gap-4 text-[13.5px] leading-relaxed">
        {coverPreview ? (
          <div className="overflow-hidden rounded-[var(--r-card)] border border-border max-md:rounded-[var(--radius-card-mobile,18px)]">
            <img
              src={coverPreview}
              alt=""
              className="h-40 w-full object-cover"
            />
          </div>
        ) : null}
        {event.description?.trim() ? (
          <p className="whitespace-pre-wrap text-muted-foreground">
            {event.description}
          </p>
        ) : (
          <p className="text-muted-foreground">{dict.detail.noDescription}</p>
        )}
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
              {formDict.location}
            </dt>
            <dd>{event.location?.trim() || dict.card.locationUnknown}</dd>
          </div>
        </dl>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <SettingsSection
        id="lead-event-cover"
        title={formDict.coverImage}
        grouped
        info={formDict.coverImageHint}
      >
        <SettingsGroupRows>
          <SettingsGroupRow>
            <LogoUpload
              label={formDict.coverImage}
              description={formDict.coverImageHint}
              hideCaption
              currentPreview={coverPreview}
              aspect={16 / 9}
              onUpload={(fileId, preview) => {
                setCoverAssetId(fileId);
                setCoverPreview(preview);
              }}
              onRemove={() => {
                setCoverAssetId(null);
                setCoverPreview(undefined);
              }}
            />
          </SettingsGroupRow>
        </SettingsGroupRows>
      </SettingsSection>

      <SettingsSection
        id="lead-event-basics"
        title={formDict.sectionBasics}
        grouped
      >
        <SettingsGroupRows>
          <SettingsGroupRow>
            <div className="min-w-0 space-y-2">
              <Label htmlFor="event-edit-name">{formDict.name}</Label>
              <Input
                id="event-edit-name"
                ref={nameInputRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={formDict.namePlaceholder}
                maxLength={LEAD_EVENT_NAME_MAX}
                aria-invalid={showValidation && !name.trim()}
                aria-describedby={
                  showValidation && !name.trim()
                    ? 'event-edit-name-error'
                    : undefined
                }
              />
              {showValidation && !name.trim() ? (
                <p
                  id="event-edit-name-error"
                  className="text-xs text-destructive"
                  role="alert"
                >
                  {formDict.nameRequired}
                </p>
              ) : null}
              <p className="text-right text-[12px] text-muted-foreground">
                {formDict.nameCount
                  .replace('{count}', String(name.length))
                  .replace('{max}', String(LEAD_EVENT_NAME_MAX))}
              </p>
            </div>
          </SettingsGroupRow>
          <SettingsGroupRow separated>
            <div className="min-w-0 space-y-2">
              <Label htmlFor="event-edit-location">{formDict.location}</Label>
              <Input
                id="event-edit-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={formDict.locationPlaceholder}
              />
            </div>
          </SettingsGroupRow>
          <SettingsGroupRow separated>
            <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0 space-y-2">
                <Label htmlFor="event-edit-start">{formDict.startsAt}</Label>
                <Input
                  id="event-edit-start"
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  className="w-full min-w-0"
                  aria-invalid={showDateRangeError}
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="event-edit-end">{formDict.endsAt}</Label>
                <Input
                  id="event-edit-end"
                  type="datetime-local"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  className="w-full min-w-0"
                  aria-invalid={showDateRangeError}
                />
              </div>
            </div>
            {showDateRangeError ? (
              <p className="mt-2 text-xs text-destructive" role="alert">
                {formDict.dateRangeError}
              </p>
            ) : null}
            {showLongRangeWarning && rangeDayCount != null ? (
              <p className="mt-2 text-xs text-muted-foreground" role="status">
                {formDict.durationLongWarning.replace(
                  '{count}',
                  String(rangeDayCount),
                )}
              </p>
            ) : null}
          </SettingsGroupRow>
          <SettingsGroupRow separated>
            <div className="min-w-0 space-y-2">
              <Label htmlFor="event-edit-description">
                {formDict.description}
              </Label>
              <Textarea
                id="event-edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={formDict.descriptionPlaceholder}
                rows={4}
                maxLength={5000}
              />
            </div>
          </SettingsGroupRow>
        </SettingsGroupRows>
      </SettingsSection>

      <SettingsSection
        id="lead-event-roster"
        title={formDict.sectionTeam}
        grouped
      >
        <SettingsGroupRows>
          <SettingsGroupRow>
            <LeadEventRosterFields
              rosterMode={rosterMode}
              onRosterModeChange={setRosterMode}
              selectedUserIds={selectedUserIds}
              onSelectedUserIdsChange={setSelectedUserIds}
              selectedTeamIds={selectedTeamIds}
              onSelectedTeamIdsChange={setSelectedTeamIds}
              members={members}
              teams={teams}
              labels={rosterLabels}
              locale={locale}
            />
          </SettingsGroupRow>
        </SettingsGroupRows>
      </SettingsSection>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          type="button"
          variant="primary"
          onClick={() => void onSubmit()}
          disabled={submitting}
        >
          {submitting ? formDict.saving : formDict.save}
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={onDelete}
          disabled={submitting}
        >
          {dict.detail.delete}
        </Button>
      </div>
    </div>
  );
}
